import React from 'react';
import _ from 'underscore';

import { UrlParamKeys } from '~commonInfra/url/UrlParamKeys';
import { Helpers } from '~commonInfra/utils/Helpers';
import { WindowLocationService } from '~src/commonInfra/services/WindowLocationService';
import { Tableau } from '~src/commonInfra/utils/TableauLog';
import { IDataAlertInfo } from '~src/dda/models/IDataAlertInfo';
import { DataAlerts } from '~src/dda/resources/DataAlerts';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';

import { IViz, VizType } from '../models/Viz';

export let VizServiceEvents = {
  VizLoadComplete: 'vizloadedcomplete'
};

export class VizService {

  constructor() {}

  public static ExplicitOriginalVizParamKey = ':original_view';
  public static InstanceIdKey = ':iid';
  private static EmbedParamKey = ':embed';
  private static IsGuestRedirectFromVizPortalParamKey = ':isGuestRedirectFromVizportal';
  private static showShareParamKey = ':showShareOptions';

  public static VizExportFileTypeRegex = /\.(pdf|csv|png)$/;

  private didAlreadyDoRedirect = false;

  public updateExplicitOriginalViz(explicitOriginalViz: boolean) {
    if (explicitOriginalViz)
      WindowLocationService.updateHashQueryParameterWithReplace(VizService.ExplicitOriginalVizParamKey, 'y', {encode: false});
    else
      WindowLocationService.deleteQueryParameterWithReplace(VizService.ExplicitOriginalVizParamKey);
  }

  public getIsExplicitOriginalViz(): boolean {
    return Helpers.BooleanQueryParamRegex.test(this.getExplicitLocationSearchValue(VizService.ExplicitOriginalVizParamKey));
  }

  public getInstanceId(): string {
    return this.getExplicitLocationSearchValue(VizService.InstanceIdKey);
  }

  public getDataAlertId(): string {
    return this.getExplicitLocationSearchValue(UrlParamKeys.EditDataAlert);
  }

  // Returns query parameter value, or null if there is no explicit value.
  // e.g. "?:embed" returns null, ":embed=blah" returns "blah"
  // Using $location.search() directly will give boolean true for keys that
  // are present with no value.
  public getExplicitLocationSearchValue(key: string): string {
    const params = WindowLocationService.getHashQueryObject() as { [key: string]: string };
    const value = params[key];
    return _.isString(value) ? value : null;
  }

  public getVizParams(): _.Dictionary<any> {
    // Remove the instance id param, as it is only used on the frontend
    return _.omit(WindowLocationService.getHashQueryObject(), VizService.InstanceIdKey);
  }

  public getIsExplicitOriginalVizFromUrl(url: string): boolean {
    // TODO: we should parse a viz object from a url instead of having several of these
    // one-off functions to get individual parts of the url.
    const match = decodeURIComponent(url).match(/[&\?]:original_view=([^&#$]+)/);
    return !!(match && match[1] && Helpers.BooleanQueryParamRegex.test(match[1]));
  }

  /**
 * Build a JS-API-ready uri from a viz object
 */
  public getUri(viz: IViz): string {
    const pathList: string[] = [WindowLocationService.getOrigin()];

    if (viz.vizType !== VizType.Admin && !!viz.siteUrlName) {
      pathList.push('t');
      pathList.push(viz.siteUrlName);
    }

    switch (viz.vizType) {
      case VizType.Admin:
        pathList.push('vizql');
        pathList.push('showadminview');
        break;
      case VizType.View:
        pathList.push('views');
        break;
      case VizType.RevisionPreview:
        pathList.push('workbooks');
        break;
      case VizType.DeviceVizPreview:
        pathList.push('views');
        break;
      default:
        throw new Error('Invalid vizType. Viz must be View, Admin, RevisionPreview, or DeviceVizPreview');
    }

    pathList.push(viz.path);
    return pathList.join('/');
  }

  /**
   * returns true if embed search param is on in url
   * Example: ?:embed=yes
   */
  public isEmbedParamOn() {
    return Helpers.BooleanQueryParamRegex.test(this.getExplicitLocationSearchValue(VizService.EmbedParamKey));
  }

  /**
   * returns true if showShareOptions search param is on in url
   * Example: ?:showShareOptions=yes
   */
  public isShowShareParamOn() {
    if (this.getExplicitLocationSearchValue(VizService.showShareParamKey) !== null) {
      return Helpers.BooleanQueryParamRegex.test(this.getExplicitLocationSearchValue(VizService.showShareParamKey));
    }
    return null;
  }

  /**
   * returns true if the end of the location path has a viz export file type.
   * Example: /workbook/view.png
   */
  public pathHasExportableFileType() {
    return VizService.VizExportFileTypeRegex.test(WindowLocationService.getFragmentWithoutQuery());
  }

  public redirectToEmbeddedMode(shouldRedirectWithoutAddingToHistory: boolean, options: { isFromGuest?: boolean, needsEmbedParam?: boolean} = {}) {
    const alertId = this.getDataAlertId();
    if (!getSessionManagementServiceInstance().inSession() || !alertId) {
      const url = this.getEmbeddedVizqlUrlForNonDataAlerts(options);
      if (!shouldRedirectWithoutAddingToHistory) {
        // When navigating from an external page to vizportal on a phone, our decision to
        // automatically redirect you to embedded mode can result in really strange behavior
        // with your browser history. The first redirect happens, but triggers the
        // $locationChangeStart handler, which causes a second redirect. To prevent this
        // second redirect from happening and mucking with the user's browser history,
        // we should only perform it when there hasn't already been a redirect processed.
        if (!this.didAlreadyDoRedirect) {
          WindowLocationService.navigateTo(url);
        } else {
          // Since VizService persists across requests, set didAlreadyDoRedirect back to
          // its default state for the next time we have to do a redirect.
          this.didAlreadyDoRedirect = false;
        }
      } else {
        WindowLocationService.navigateToWithoutAddingToHistory(url);
        this.didAlreadyDoRedirect = true;
      }
    } else {
      // getEmbeddedVizqlUrlForDataAlerts returns a promise, so WindowLocationService navigates to the url returned by the callback in the promise
      this.getEmbeddedVizqlUrlForDataAlerts(alertId, options).then((url: string) => {
        WindowLocationService.navigateToWithoutAddingToHistory(url);
      });
    }
  }

  // TFSID949509
  // this looks like it should also include redirect_to_favorite_view but everything is working fine??
  // my guess is that this can be deleted after all the viz code moves to react router. Or now.
  public shouldRedirectWithoutAddingToHistory(oldUrl: string) {
    // The /redirect_to_view URL is not supposed to add itself to the user's browser
    // history, since it uses {location: 'replace'}. However, there is potentially
    // a bug in our version of angular-ui-router, since it shows up in the history
    // anyway. This causes issues when we redirect to the embedded version of a view,
    // since hitting the back button sends us to /redirect_to_view, which sends us
    // back to the view we were just looking at.
    //
    // To fix this, try and detect when the oldUrl is /redirect_to_view. If it is,
    // we will return true to eventually tell the WindowLocationService to
    // navigate the user to the view without adding to the history. This function can be
    // simplified to "return false;" if and when we update the router so that
    // /redirect_to_view does not add itself to the history.
    return this.isRedirectingToVizUrl(oldUrl);
  }

  public isRedirectingToVizUrl(url: string): boolean {
    const regexString_Redirect = '.*/redirect_to_view/[0-9]+';
    const regExp_Redirect = new RegExp(regexString_Redirect);

    const regexString_CustomViewRedirect = '.*/redirect_to_custom_view/[0-9]+';
    const regExp_CustomViewRedirect = new RegExp(regexString_CustomViewRedirect);

    const regexString_FavoriteRedirect = '.*/redirect_to_favorite_view/[0-9]+';
    const regExp_FavoriteRedirect = new RegExp(regexString_FavoriteRedirect);
    const isUrlRedirectToView = regExp_Redirect.test(url) || regExp_CustomViewRedirect.test(url) || regExp_FavoriteRedirect.test(url);
    return isUrlRedirectToView;
  }

  /**
  * Go to embedded version of the current url if it is not a data alert.
  * Throws an Error if the current url is a data alert. If embed param
  * is not on, we pass in needsEmbedParam as true so that getVizqlUrl turns it on.
  */
  public getEmbeddedVizqlUrlForNonDataAlerts(options: { isFromGuest?: boolean } = {}) {
    return this.getVizqlUrl({ isFromGuest: options.isFromGuest, needsEmbedParam: !this.isEmbedParamOn() });
  }

  /**
   * Go to embedded version of the current url if it is a data alert.
   * Throws an Error if the current url is not a data alert. If embed param
   * is not on, we pass in needsEmbedParam as true so that getVizqlUrl turns it on.
   */
  public getEmbeddedVizqlUrlForDataAlerts(alertId: string, options: { isFromGuest?: boolean } = {}): Promise<IDataAlertInfo | string> {
    return DataAlerts.getById(alertId)
      .then((dataAlert: IDataAlertInfo) => {
        WindowLocationService.setHashQueryParameter(UrlParamKeys.DeviceType, dataAlert.deviceType.toLowerCase(), {encode: false});
        return this.getVizqlUrl({ isFromGuest: options.isFromGuest, needsEmbedParam: !this.isEmbedParamOn() });
      }).catch((error: TypeError) => {
        Tableau.Logger().error('Unable to find valid alert with Id: ' + alertId);
        return this.getVizqlUrl({ isFromGuest: options.isFromGuest });
      });
  }

  public getVizqlUrl(options: { isFromGuest?: boolean, needsEmbedParam?: boolean } = {}): string {
    if (options.isFromGuest) {
      WindowLocationService.setHashQueryParameter(VizService.IsGuestRedirectFromVizPortalParamKey, 'y', {encode: false});
    }
    if (options.needsEmbedParam) {
      // this also needs to {encode: false} // however causing seemingly unrelated integratino test failures
      WindowLocationService.setHashQueryParameter(VizService.EmbedParamKey, 'y');
    }
    return this.decodeVizqlUrl();
  }

  public decodeVizqlUrl(): string {
    let path = window.location.hash.replace(/\/site/, '/t');
    // Return the relative path without the '#' character
    let relativePath = (path.charAt(0) === '#') ? path.substring(1) : path;
    return relativePath;
  }
}

export const VizServiceContext = React.createContext<VizService>(null);
